import React, { Dispatch, SetStateAction, useState } from 'react'
import Select from 'react-select'
import { ToolTipUI } from '../UI/ToolTipUI'
import { ToolTipUIForModal } from '../UI/ToolTipUIForModal'
import { Field, ErrorMessage, getIn, FieldProps } from 'formik'
import { useThemeMode } from '../../../../../_metronic/partials'

// Define the type for options
type OptionType = { id?: string, value: string | number; label: string } | string

interface SelectSearchInputProps {
  fieldName: string
  label?: string
  toolTipText?: string
  placeholder: string
  formik: any
  options: any[]
  margin?: string
  width?: number
  isStarRequired?: boolean
  isTooltipForModal?: boolean
  isTooltipNotRequired?: boolean
  isMulti?: boolean
  disabled?: boolean
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  ariaLabel?: string
  setSelectedLevel1Option?: Dispatch<SetStateAction<any>>
  setSelectedLevel2Option?: Dispatch<SetStateAction<any>>
  // Optional: If you want to support multi-select
}

// Helper function to transform options for react-select
const transformOptions = (
  options: OptionType[]
): { id?: string | number; value: string | number; label: string }[] => {
  return options.map((option) => {
    if (typeof option === 'string') {
      return { value: option, label: option };
    } else {
      return {
        ...(option.id && { id: option.id }), // Include id only if it exists
        value: option.value,
        label: option.label,
      };
    }
  });
};



const SelectSearchInput: React.FC<SelectSearchInputProps> = ({
  fieldName,
  label,
  toolTipText,
  placeholder,
  formik,
  options,
  margin,
  width,
  isStarRequired,
  isTooltipForModal,
  isTooltipNotRequired,
  isMulti,
  disabled,
  onFocus,
  onBlur,
  ariaLabel,
  setSelectedLevel1Option,
  setSelectedLevel2Option,
}) => {
  const [, setSelectedOptions] = useState<any>(null)

  // eslint-disable-line

  const { mode } = useThemeMode()
  const handleChange = async (selectedOptions: any) => {
    setSelectedOptions(selectedOptions)
    formik.setFieldValue(fieldName, selectedOptions)
    if (fieldName === 'country') {
      formik.setFieldValue('state', null)
    } else if (fieldName === 'participating') {
      formik.setFieldValue('participatingCap', '')
      await formik.validateForm()

      formik.setFieldError('participatingCap', undefined)
    } else if (fieldName === 'valuationCapType') {
      formik.setFieldValue('valuationCap', '')
    } else if (fieldName === 'valuationFloorType') {
      formik.setFieldValue('valuationFloor', '')
    } else if (fieldName === 'conversionParameter') {
      formik.setFieldValue('conversionParameterValue', '')
    } else if (fieldName === "level1" && setSelectedLevel1Option) {
      setSelectedLevel1Option(selectedOptions)
    } else if (fieldName === "level2" && setSelectedLevel2Option) {
      setSelectedLevel2Option(selectedOptions)
    }
  }

  // Get the current field value from Formik
  const fieldValue = getIn(formik.values, fieldName)

  // Custom styles for the select component
  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      boxShadow: 'none',
      fontSize: '12px',
      fontWeight: '500',
      borderRadius: '0.5rem',
      border: `${formik.touched[fieldName] && formik.errors[fieldName]
        ? '1px solid #f1416c'
        : formik.touched[fieldName] && !formik.errors[fieldName]
          ? '1px solid #50cd89'
          : mode === 'dark'
            ? 'none'
            : '1px solid #ddd'
        }`,
      '&:hover': {
        border: `${formik.touched[fieldName] && formik.errors[fieldName]
          ? '1px solid #f1416c'
          : formik.touched[fieldName] && !formik.errors[fieldName]
            ? '1px solid #50cd89'
            : mode === 'dark'
              ? 'none'
              : '1px solid #ddd'
          }`,
      },
      background: `${formik.touched[fieldName] && formik.errors[fieldName]
        ? 'rgba(241, 65, 108, 0.2)'
        : formik.touched[fieldName] && !formik.errors[fieldName]
          ? 'white'
          : mode === 'dark'
            ? '#181c32'
            : disabled
              ? '#eff2f5'
              : 'white'
        }`,
    }),
    placeholder: (provided: any) => ({
      ...provided,
      fontSize: '12px',
      color: '#a1a5b7',
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: mode === 'dark' ? '#181c32' : 'white',
      borderRadius: '0.5rem',
    }),
    option: (provided: any, state: { isFocused: boolean; isSelected: boolean }) => ({
      ...provided,
      backgroundColor: state.isFocused
        ? (mode === 'dark' ? '#3f434d' : '#e9ecef')
        : state.isSelected
          ? (mode === 'dark' ? '#3f434d' : '#f1f1f1')
          : 'transparent',
      color: state.isSelected || state.isFocused
        ? (mode === 'dark' ? 'white' : 'black')
        : mode === 'dark'
          ? '#a1a5b7'
          : 'black',
      fontSize: '12px',
      padding: '8px 12px',
    }),
  }
  return (
    <div className={`fv-row mb-8 ${margin ? margin : ''} ${width ? `col-md-${width}` : ''}`}>
      {label && (
        <label className='d-flex form-label font-size-12 text-dark text-capitalize ms-0 ms-md-0'>
          {label}
          {!isTooltipNotRequired && (
            <>
              {isStarRequired && '*'}
              {isTooltipForModal ? (
                <ToolTipUIForModal tooltipText={toolTipText} />
              ) : (
                <ToolTipUI tooltipText={toolTipText} />
              )}
            </>
          )}
        </label>
      )}
      <Field name={fieldName} onBlur={onBlur}>
        {({ field }: FieldProps) => (
          <Select
            options={transformOptions(options)}
            onChange={handleChange}
            placeholder={placeholder}
            styles={customStyles}
            isMulti={isMulti}
            value={fieldValue}
            isDisabled={disabled}
            onBlur={onBlur}
            onFocus={onFocus}
            className={mode === 'dark' ? 'text-dark' : 'text-white'}
            aria-label={ariaLabel}
            classNamePrefix='react-select'
          />
        )}
      </Field>
      <div className='text-danger mt-2'>
        <ErrorMessage name={fieldName} />
      </div>
    </div>
  )
}

export default SelectSearchInput
